/**
 * «Dette bør du se på» på ny Måltall (VIB-2991). Faste regler mot dataene,
 * uten AI. Hver sjekk gir ett funn med tall og listen bak, eller ingenting.
 * Ren logikk, testet i maaltall-checks.test.ts.
 */
import { formatDate, formatHoursWhole, formatNOK, MONTH_NAMES_NB } from '@/lib/format';

export type CheckType =
  | 'ready_to_close'
  | 'overdue_rest'
  | 'draft_invoice'
  | 'placeholder_deadline'
  | 'offers_no_probability'
  | 'pending_approval'
  | 'unlogged_months'
  | 'unclosed_months'
  | 'approved_without_hours'
  | 'hours_without_task';

export type CheckTone = 'miss' | 'near' | 'neutral';
export type CheckUnit = 'kr' | 'geek' | 't' | 'mnd';

export interface CheckItem {
  id: string;
  title: string;
  sub?: string;
  value: number;
  to?: string;
}

export interface CheckFinding {
  type: CheckType;
  /** Sjekktype og antall. Endres når funnet endres, så en vurdering kan knyttes til det den gjaldt. */
  key: string;
  tone: CheckTone;
  title: string;
  body: string;
  who: string;
  value: number;
  unit: CheckUnit;
  /** Regelen sjekken følger, vist i sidepanelet. */
  rule: string;
  items: CheckItem[];
  source: { to: string; label: string };
}

export interface CheckProject {
  id: string;
  name: string;
  projectCode: string;
  advisorName: string;
  deadline: string | null;
  /** Gjenstår å fakturere, eks. mva. */
  rest: number;
  bfEstimated: number;
}

export interface CheckInputs {
  /** Oslo-dato YYYY-MM-DD. */
  today: string;
  activeProjects: CheckProject[];
  draftInvoices: Array<{ id: string; projectId: string; projectName: string; amount: number; invoiceDate: string | null; projectDeadline: string | null }>;
  uncategorizedOffers: Array<{ id: string; title: string; customerName: string | null; bfKr: number }>;
  pendingTasks: Array<{ id: string; projectId: string; projectName: string; label: string; hours: number; leadName: string | null }>;
  approvedTasks: Array<{ id: string; budgetLineId: string; userId: string; projectId: string; projectName: string; label: string; hours: number }>;
  timeEntries: Array<{ assignmentId: string | null; budgetLineId: string | null; userId: string | null; projectId: string | null; hours: number }>;
  /** Kundeprosjekter (ikke interne, ikke slettet) med navn. */
  customerProjects: Map<string, string>;
  employees: Array<{
    userId: string;
    fullName: string;
    months: Array<{ month: number; loggedHours: number; capacity: number; confirmed: boolean }>;
  }>;
}

/** En måned med kapasitet regnes som nesten uten timer under denne andelen. */
export const UNLOGGED_SHARE = 0.25;
/** Månedslukking nudges fra den 6. Før det regnes forrige måned som åpen. */
export const MONTH_CLOSE_DAY = 6;

const TONE_RANK: Record<CheckTone, number> = { miss: 0, near: 1, neutral: 2 };
const UNIT_RANK: Record<CheckUnit, number> = { kr: 0, geek: 0, t: 1, mnd: 2 };
const byValue = <T extends { value: number }>(a: T, b: T) => b.value - a.value;
const sum = (xs: Array<{ value: number }>) => xs.reduce((s, x) => s + x.value, 0);
const project = (id: string) => `/prosjekter/${id}`;

function finding(
  f: Omit<CheckFinding, 'key' | 'value' | 'items'> & { items: CheckItem[] },
): CheckFinding | null {
  if (f.items.length === 0) return null;
  const items = [...f.items].sort(byValue);
  return { ...f, items, value: sum(items), key: `${f.type}:${items.length}` };
}

export function runMaaltallChecks(input: CheckInputs): CheckFinding[] {
  const { today } = input;
  const passed = (d: string | null) => d != null && d < today;
  const [year, month, day] = today.split('-').map(Number);
  const out: Array<CheckFinding | null> = [];

  const ready = input.activeProjects.filter((p) => passed(p.deadline) && p.rest <= 1);
  out.push(finding({
    type: 'ready_to_close', tone: 'miss', unit: 'geek', who: 'Prosjektledere',
    title: `${ready.length} prosjekter er ferdig fakturert, men ikke avsluttet`,
    body: 'Deadline passert. GEEK teller som prognose til de avsluttes.',
    rule: 'Aktive prosjekter der estimert deadline har passert og alt er fakturert.',
    items: ready.map((p) => ({ id: p.id, title: `${p.projectCode} ${p.name}`, sub: `Deadline ${formatDate(p.deadline)}`, value: p.bfEstimated, to: project(p.id) })),
    source: { to: '/prosjekter', label: 'Åpne Prosjekter' },
  }));

  const overdue = input.activeProjects.filter((p) => passed(p.deadline) && p.rest > 1);
  out.push(finding({
    type: 'overdue_rest', tone: 'near', unit: 'kr', who: 'Rådgivere',
    title: `${overdue.length} prosjekter har passert deadline med noe igjen å fakturere`,
    body: 'Resten legges i inneværende måned i prognosen.',
    rule: 'Aktive prosjekter der estimert deadline har passert og salgssum minus fakturert er over 0, eks. mva.',
    items: overdue.map((p) => ({ id: p.id, title: `${p.projectCode} ${p.name}`, sub: `Deadline ${formatDate(p.deadline)} · rådgiver ${p.advisorName || 'ikke satt'}`, value: p.rest, to: project(p.id) })),
    source: { to: '/faktura', label: 'Åpne Faktura' },
  }));

  const drafts = input.draftInvoices.filter((i) => passed(i.projectDeadline));
  out.push(finding({
    type: 'draft_invoice', tone: 'neutral', unit: 'kr', who: 'Faktura',
    title: drafts.length === 1 ? 'En fakturakladd er ikke sendt' : `${drafts.length} fakturakladder er ikke sendt`,
    body: 'Prognosen teller kladder som fakturert.',
    rule: 'Fakturaer med status kladd på aktive prosjekter der deadline har passert.',
    items: drafts.map((i) => ({ id: i.id, title: i.projectName, sub: `Kladd datert ${formatDate(i.invoiceDate)}`, value: i.amount, to: project(i.projectId) })),
    source: { to: '/faktura', label: 'Åpne Faktura' },
  }));

  const placeholder = input.activeProjects.filter((p) => p.deadline?.endsWith('-12-31') && !passed(p.deadline));
  out.push(finding({
    type: 'placeholder_deadline', tone: 'near', unit: 'kr', who: 'Prosjektledere',
    title: `${placeholder.length} prosjekter har deadline 31.12`,
    body: 'Trolig plassholder. Hele resten havner i desember i prognosen.',
    rule: 'Aktive prosjekter med estimert deadline 31. desember. Beløpet er det som gjenstår å fakturere, eks. mva.',
    items: placeholder.map((p) => ({ id: p.id, title: `${p.projectCode} ${p.name}`, sub: `Rådgiver ${p.advisorName || 'ikke satt'}`, value: p.rest, to: project(p.id) })),
    source: { to: '/prosjekter', label: 'Åpne Prosjekter' },
  }));

  out.push(finding({
    type: 'offers_no_probability', tone: 'near', unit: 'geek', who: 'Selgere',
    title: `${input.uncategorizedOffers.length} tilbud mangler sannsynlighet`,
    body: 'Pipen kan ikke vektes uten sannsynlighet.',
    rule: 'Sendte tilbud som ikke har gått ut på dato, uten sannsynlighet satt.',
    items: input.uncategorizedOffers.map((o) => ({ id: o.id, title: o.title, sub: o.customerName ?? undefined, value: o.bfKr, to: `/tilbud/${o.id}` })),
    source: { to: '/tilbud', label: 'Åpne Tilbud' },
  }));

  const leads = [...new Set(input.pendingTasks.map((t) => t.leadName).filter(Boolean))] as string[];
  out.push(finding({
    type: 'pending_approval', tone: 'near', unit: 't',
    who: leads.length === 1 ? leads[0] : 'Prosjektledere',
    title: `${input.pendingTasks.length} ferdige oppgaver venter på godkjenning`,
    body: 'De teller i faktureringsgraden først når de godkjennes.',
    rule: 'Oppgaver med status fullført som ikke er godkjent. Timene er solgte timer på oppgaven.',
    items: input.pendingTasks.map((t) => ({ id: t.id, title: `${t.label} · ${t.projectName}`, sub: t.leadName ? `PL ${t.leadName}` : undefined, value: t.hours, to: project(t.projectId) })),
    source: { to: '/mine-oppgaver', label: 'Åpne Mine oppgaver' },
  }));

  const closedThrough = month - 1;
  const unlogged: CheckItem[] = [];
  const unclosed: CheckItem[] = [];
  for (const e of input.employees) {
    for (const m of e.months) {
      if (m.capacity <= 0 || m.month > closedThrough) continue;
      if (m.capacity >= 20 && m.loggedHours < m.capacity * UNLOGGED_SHARE) {
        unlogged.push({ id: `${e.userId}-${m.month}`, title: e.fullName, sub: `${MONTH_NAMES_NB[m.month - 1]} ${year} · ${formatHoursWhole(m.loggedHours)} ført av ${formatHoursWhole(m.capacity)}`, value: m.capacity - m.loggedHours });
      }
      const closeable = m.month < closedThrough || day >= MONTH_CLOSE_DAY;
      if (!m.confirmed && closeable) {
        unclosed.push({ id: `${e.userId}-${m.month}`, title: e.fullName, sub: `${MONTH_NAMES_NB[m.month - 1]} ${year}`, value: 1 });
      }
    }
  }
  const people = (items: CheckItem[]) => new Set(items.map((i) => i.title)).size;
  out.push(finding({
    type: 'unlogged_months', tone: 'miss', unit: 't', who: 'Ansatte og admin',
    title: `${people(unlogged)} personer har måneder nesten uten timer`,
    body: 'Er ferie eller fravær ikke registrert, trekker kapasiteten faktureringsgraden ned.',
    rule: `Lukkede måneder der en ansatt med kapasitet har ført under ${UNLOGGED_SHARE * 100} % av kapasiteten. Timene er kapasitet minus førte timer.`,
    items: unlogged,
    source: { to: '/ansatte', label: 'Åpne Ansatte' },
  }));
  out.push(finding({
    type: 'unclosed_months', tone: 'near', unit: 'mnd', who: 'Ansatte',
    title: `${people(unclosed)} personer har ikke lukket alle måneder`,
    body: 'Timene i månedene er ikke bekreftet.',
    rule: `Måneder med kapasitet før inneværende måned som ikke er lukket. Forrige måned regnes med fra den ${MONTH_CLOSE_DAY}.`,
    items: unclosed,
    source: { to: '/ansatte', label: 'Åpne Ansatte' },
  }));

  const logged = new Set<string>();
  for (const te of input.timeEntries) {
    if (te.assignmentId) logged.add(`a:${te.assignmentId}`);
    if (te.budgetLineId && te.userId) logged.add(`l:${te.budgetLineId}:${te.userId}`);
  }
  const noHours = input.approvedTasks.filter((t) => t.hours > 0 && !logged.has(`a:${t.id}`) && !logged.has(`l:${t.budgetLineId}:${t.userId}`));
  out.push(finding({
    type: 'approved_without_hours', tone: 'neutral', unit: 't', who: 'Prosjektledere',
    title: `${noHours.length} godkjente oppgaver har ingen førte timer`,
    body: 'De teller som fakturerbare. Kan stemme for utleie, ellers er timene trolig ført et annet sted.',
    rule: 'Oppgaver godkjent i år uten timer ført på oppgaven eller på samme budsjettlinje av samme person.',
    items: noHours.map((t) => ({ id: t.id, title: `${t.label} · ${t.projectName}`, value: t.hours, to: project(t.projectId) })),
    source: { to: '/prosjekter', label: 'Åpne Prosjekter' },
  }));

  const loose = new Map<string, number>();
  for (const te of input.timeEntries) {
    if (te.assignmentId || te.budgetLineId || !te.projectId || !input.customerProjects.has(te.projectId)) continue;
    loose.set(te.projectId, (loose.get(te.projectId) ?? 0) + te.hours);
  }
  out.push(finding({
    type: 'hours_without_task', tone: 'neutral', unit: 't', who: 'Ansatte',
    title: `Timer uten oppgave på ${loose.size} kundeprosjekter`,
    body: 'Timene teller som brukt, men ikke mot noen solgt oppgave.',
    rule: 'Timer i år på kundeprosjekter som verken er koblet til en oppgave eller en budsjettlinje.',
    items: [...loose].map(([id, hours]) => ({ id, title: input.customerProjects.get(id) ?? 'Prosjekt', value: hours, to: project(id) })),
    source: { to: '/prosjekter', label: 'Åpne Prosjekter' },
  }));

  return out
    .filter((f): f is CheckFinding => f !== null)
    .sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone] || UNIT_RANK[a.unit] - UNIT_RANK[b.unit] || b.value - a.value);
}

export function formatCheckValue(value: number, unit: CheckUnit): string {
  if (unit === 't') return formatHoursWhole(value);
  if (unit === 'mnd') return `${value} ${value === 1 ? 'måned' : 'måneder'}`;
  const kr = formatNOK(Math.round(value));
  return unit === 'geek' ? `${kr} GEEK` : kr;
}

